import { useInstructions } from '@/api'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { InstructionScope } from '@/lib/enum-display'
import { InstructionList } from './instruction-list'
import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { getDefaultTemplate } from '@/lib/instruction-template'
import { Link } from '@tanstack/react-router'

export function InstructionOverlays({
  inboxId,
  onDirtyChange,
  useStarter = false,
}: {
  inboxId?: string
  onDirtyChange?: (dirty: boolean) => void
  useStarter?: boolean
}) {
  const { user } = useAuth()
  const userName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
  const [dirty, setDirty] = useState({ overall: false, inbox: false })
  const overallDirty = useCallback(
    (value: boolean) =>
      setDirty((current) =>
        current.overall === value ? current : { ...current, overall: value },
      ),
    [],
  )
  const inboxDirty = useCallback(
    (value: boolean) =>
      setDirty((current) =>
        current.inbox === value ? current : { ...current, inbox: value },
      ),
    [],
  )
  useEffect(() => {
    onDirtyChange?.(dirty.overall || dirty.inbox)
  }, [dirty, onDirtyChange])
  const overall = useInstructions(
    { scope: InstructionScope.USER },
    { gcTime: 0 },
  )
  const account = useInstructions(
    { scope: InstructionScope.ACCOUNT, connectedAccountId: inboxId },
    { enabled: !!inboxId, gcTime: 0 },
  )
  return (
    <div className="sentry-block ph-no-capture space-y-6">
      {!inboxId &&
        (overall.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : overall.isError ? (
          <div role="alert" className="space-y-2">
            <p>Your overall instructions could not load.</p>
            <Button variant="outline" onClick={() => overall.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <InstructionList
            instructions={overall.data ?? []}
            scope={InstructionScope.USER}
            onDirtyChange={overallDirty}
            title="Overall instructions"
            description="Apply to all your inboxes."
            emptyTitle="No overall instructions"
            emptyDescription="Add instructions that apply across your inboxes."
          />
        ))}
      {inboxId &&
        (account.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : account.isError ? (
          <div role="alert" className="space-y-2">
            <p>This inbox’s instructions could not load.</p>
            <Button variant="outline" onClick={() => account.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <InstructionList
            instructions={account.data ?? []}
            scope={InstructionScope.ACCOUNT}
            onDirtyChange={inboxDirty}
            scopedId={inboxId}
            variant="section"
            title="Inbox instructions"
            emptyTitle="No inbox instructions yet"
            emptyDescription="Your overall instructions still apply."
            starter={
              useStarter && !account.data?.length
                ? {
                    title: 'Email Processing Instructions',
                    content: getDefaultTemplate(userName),
                  }
                : undefined
            }
          />
        ))}
      {inboxId && (
        <section className="space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <h2 className="font-semibold">Overall instructions</h2>
            <Link to="/settings" className="text-sm font-medium text-primary hover:underline">
              Edit in Settings
            </Link>
          </div>
          {overall.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : overall.isError ? (
            <p role="alert" className="text-sm">Your overall instructions couldn’t load.</p>
          ) : overall.data?.some((item) => item.is_active) ? (
            <ul className="space-y-2">
              {overall.data
                .filter((item) => item.is_active)
                .map((item) => (
                  <ReadOnlyInstruction key={item.uuid} title={item.title} content={item.content} />
                ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">None yet.</p>
          )}
        </section>
      )}
    </div>
  )
}

function ReadOnlyInstruction({ title, content }: { title: string; content: string }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <li className="rounded-lg bg-muted/50 p-3">
      <h3 className="break-words text-sm font-semibold">{title}</h3>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
        className={`mt-1 block w-full whitespace-pre-line break-words text-left text-sm text-muted-foreground ${expanded ? '' : 'line-clamp-4'}`}
      >
        {content}
      </button>
    </li>
  )
}
